/**
 * expr/walker.ts — a sandboxed, CEL-compatible expression evaluator.
 *
 * Why this exists: the `formula` gate, edge-connection `expr`s, and metric
 * `expr` rollups all need to evaluate user-authored expressions like
 * `burst_size > 100 ? capacity * 0.8 : rate` or `nodes.delivery.stock > 0.9 *
 * self.capacity ? 0 : self.inflow`. We do NOT use `eval` or `new Function`
 * (the previous `evalFormula` did, despite the comment claiming otherwise).
 * Instead jsep parses the string to an AST, we project it onto our own typed
 * AST (rejecting anything outside the allowed surface), and a hand-written
 * walker evaluates it against a numeric scope.
 *
 * The allowed surface mirrors a CEL subset:
 *   - literals (number)
 *   - identifiers (resolved against scope; missing → ExprError, NOT silently 0)
 *   - member access `a.b.c` and index `a['k']` (CEL's map-select)
 *   - arithmetic `+ - * / %`
 *   - comparison `> < >= <= == !=`
 *   - logical `&& ||` (short-circuit)
 *   - ternary `c ? a : b`
 *   - `has(a.b)` (presence test)
 *
 * Numbers in, number out. Booleans are produced by comparisons/logical ops and
 * consumed by ternary conditions; the final result must coerce to a number.
 *
 * Python mirror: `backend/expr/walker.py` implements the same AST + operators
 * + errors. `corpus.test.ts` / `corpus.test.py` run the same ~30 formulas on
 * both sides and must agree — that's the parity guarantee. The public
 * expression-string surface is CEL-compatible, so swapping in @bufbuild/cel
 * later is a drop-in if/when it leaves beta.
 */
import jsep from 'jsep';

// ─── AST ────────────────────────────────────────────────────────────────────
export type ExprNode =
  | { type: 'Literal'; value: number }
  | { type: 'Identifier'; name: string }
  | { type: 'Member'; object: ExprNode; property: ExprNode; computed: boolean }
  | { type: 'Unary'; op: '-' | '!'; argument: ExprNode }
  | { type: 'Binary'; op: BinaryOp; left: ExprNode; right: ExprNode }
  | { type: 'Logical'; op: '&&' | '||'; left: ExprNode; right: ExprNode }
  | { type: 'Conditional'; test: ExprNode; consequent: ExprNode; alternate: ExprNode }
  | { type: 'Call'; callee: 'has'; args: [ExprNode] };

type BinaryOp = '+' | '-' | '*' | '/' | '%' | '>' | '<' | '>=' | '<=' | '==' | '!=';

/** Scope: nested maps of strings → numbers (or sub-maps for member access).
 *  `has()` works on map keys. Numbers are the leaves.
 *
 *  Typed loosely (`Record<string, unknown>`) so callers (compute.ts builds a
 *  rich scope) don't have to fight a recursive type. Internal walkers narrow. */
export type Scope = number | { [key: string]: Scope } | undefined;

export class ExprError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ExprError';
  }
}

// ─── Parse (jsep → typed AST) ───────────────────────────────────────────────
const BINARY_OPS = new Set<string>(['+', '-', '*', '/', '%', '>', '<', '>=', '<=', '==', '!=']);

/** Parse + project. Throws ExprError on disallowed syntax. */
export function parse(expr: string): ExprNode {
  let raw: jsep.Expression;
  try {
    raw = jsep(expr);
  } catch (e) {
    throw new ExprError(`parse error: ${e instanceof Error ? e.message : String(e)}`);
  }
  return project(raw);
}

function project(node: jsep.Expression): ExprNode {
  switch (node.type) {
    case 'Literal':
      if (typeof (node as unknown as jsep.Literal).value !== 'number') {
        throw new ExprError(`literal must be a number, got ${JSON.stringify((node as unknown as jsep.Literal).value)}`);
      }
      return { type: 'Literal', value: (node as unknown as jsep.Literal).value as number };

    case 'Identifier': {
      const name = (node as unknown as jsep.Identifier).name;
      if (name === 'true') return { type: 'Literal', value: 1 };
      if (name === 'false') return { type: 'Literal', value: 0 };
      return { type: 'Identifier', name };
    }

    case 'MemberExpression': {
      const m = node as unknown as jsep.MemberExpression;
      const object = project(m.object);
      let property: ExprNode;
      if (m.computed) {
        property = project(m.property);
      } else {
        const propName = (m.property as unknown as jsep.Identifier).name;
        property = { type: 'Literal', value: stringHashLookupPlaceholder(propName) };
      }
      return { type: 'Member', object, property, computed: m.computed };
    }

    case 'UnaryExpression': {
      const u = node as unknown as jsep.UnaryExpression;
      if (u.operator !== '-' && u.operator !== '!') {
        throw new ExprError(`unary operator '${u.operator}' not supported`);
      }
      return { type: 'Unary', op: u.operator, argument: project(u.argument) };
    }

    case 'BinaryExpression': {
      const b = node as unknown as jsep.BinaryExpression;
      const raw = b.operator as string;
      // jsep may emit && / || as either LogicalExpression or BinaryExpression
      // depending on plugins; accept both here.
      if (raw === '&&' || raw === '||') {
        return { type: 'Logical', op: raw, left: project(b.left), right: project(b.right) };
      }
      let op: BinaryOp;
      if (raw === '===' || raw === '==') op = '==';
      else if (raw === '!==' || raw === '!=') op = '!=';
      else op = raw as BinaryOp;
      if (!BINARY_OPS.has(op)) throw new ExprError(`binary operator '${b.operator}' not supported`);
      return { type: 'Binary', op, left: project(b.left), right: project(b.right) };
    }

    case 'LogicalExpression': {
      const l = node as unknown as { type: string; operator: string; left: jsep.Expression; right: jsep.Expression };
      if (l.operator !== '&&' && l.operator !== '||') {
        throw new ExprError(`logical operator '${l.operator}' not supported`);
      }
      return { type: 'Logical', op: l.operator as '&&' | '||', left: project(l.left), right: project(l.right) };
    }

    case 'ConditionalExpression': {
      const c = node as unknown as jsep.ConditionalExpression;
      return {
        type: 'Conditional',
        test: project(c.test),
        consequent: project(c.consequent),
        alternate: project(c.alternate),
      };
    }

    case 'CallExpression': {
      const c = node as unknown as jsep.CallExpression;
      const callee = c.callee as unknown as jsep.Identifier;
      if (callee.type !== 'Identifier' || callee.name !== 'has') {
        throw new ExprError(`only has() calls supported, got '${callee.type === 'Identifier' ? callee.name : '...'}'`);
      }
      const args = c.arguments ?? [];
      if (args.length !== 1) throw new ExprError(`has() takes exactly 1 argument, got ${args.length}`);
      return { type: 'Call', callee: 'has', args: [project(args[0])] };
    }

    default:
      throw new ExprError(`unsupported syntax node: ${node.type}`);
  }
}

/** Non-computed member access (a.b) needs a string key, but our walker's
 *  Member.property is an ExprNode (numeric literal). We carry the string in a
 *  side-table keyed by an integer, so the walker can recover it. This is a
 *  pragmatic workaround for keeping the AST JSON-serialisable for the Python
 *  mirror. The mirror reads `property.value` as the lookup id. */
const stringTable: string[] = [];
function stringHashLookupPlaceholder(s: string): number {
  // Stable: same string → same id within a process. The Python mirror uses the
  // same scheme.
  let idx = stringTable.indexOf(s);
  if (idx < 0) {
    idx = stringTable.length;
    stringTable.push(s);
  }
  return idx;
}
/** Walker calls this to recover the string key from a placeholder id. */
export function _lookupStringPlaceholder(id: number): string {
  return stringTable[id];
}

// ─── Evaluate ───────────────────────────────────────────────────────────────
/** Evaluate a parsed AST against a scope. Returns a number. */
export function evaluate(node: ExprNode, scope: Scope): number {
  switch (node.type) {
    case 'Literal':
      return node.value;

    case 'Identifier': {
      const v = lookup(scope, node.name);
      if (v === undefined) throw new ExprError(`unknown identifier: ${node.name}`);
      return toNum(v);
    }

    case 'Member': {
      // Computed: a[k] — k must resolve to a number that's a string-placeholder id.
      // Non-computed: a.k — property.value is the placeholder id; recover the string.
      const obj = resolveObject(node.object, scope);
      let key: string;
      if (node.computed) {
        // For computed access on our map scope, the property should have been
        // a literal string — but our AST only allows numeric literals. We
        // support computed access only via the placeholder scheme too: the
        // jsep projector folds identifier properties to placeholders regardless
        // of computed-ness. (Computed string literals are rare in our domain;
        // reject them for now.)
        throw new ExprError('computed member access with string keys not supported; use a.b syntax');
      } else {
        const id = (node.property as { type: string; value: number }).value;
        key = _lookupStringPlaceholder(id);
      }
      if (!(key in obj)) throw new ExprError(`no such field: ${key}`);
      return toNum(obj[key]);
    }

    case 'Unary': {
      const v = evaluate(node.argument, scope);
      return node.op === '-' ? -v : v === 0 ? 1 : 0;
    }

    case 'Binary': {
      const l = evaluate(node.left, scope);
      const r = evaluate(node.right, scope);
      switch (node.op) {
        case '+': return l + r;
        case '-': return l - r;
        case '*': return l * r;
        case '/': return r === 0 ? 0 : l / r; // CEL-like: avoid throwing on /0
        case '%': return r === 0 ? 0 : l % r;
        case '>': return l > r ? 1 : 0;
        case '<': return l < r ? 1 : 0;
        case '>=': return l >= r ? 1 : 0;
        case '<=': return l <= r ? 1 : 0;
        case '==': return l === r ? 1 : 0;
        case '!=': return l !== r ? 1 : 0;
      }
    }

    case 'Logical': {
      const l = evaluate(node.left, scope);
      if (node.op === '&&') return l === 0 ? 0 : evaluate(node.right, scope);
      return l !== 0 ? 1 : evaluate(node.right, scope);
    }

    case 'Conditional': {
      const t = evaluate(node.test, scope);
      return t !== 0 ? evaluate(node.consequent, scope) : evaluate(node.alternate, scope);
    }

    case 'Call': {
      if (node.callee !== 'has') throw new ExprError(`unknown function: ${node.callee}`);
      // has(a.b) — does the path exist in scope? Doesn't throw on missing.
      const arg = node.args[0];
      if (arg.type !== 'Member') throw new ExprError('has() expects a member expression');
      try {
        const obj = resolveObject(arg.object, scope);
        const id = (arg.property as { type: string; value: number }).value;
        const key = _lookupStringPlaceholder(id);
        return key in obj ? 1 : 0;
      } catch {
        return 0;
      }
    }
  }
}

function lookup(scope: Scope, key: string): Scope {
  if (scope === undefined || typeof scope !== 'object') return undefined;
  return (scope as Record<string, Scope>)[key];
}
function resolveObject(node: ExprNode, scope: Scope): Record<string, Scope> {
  if (node.type === 'Identifier') {
    const v = lookup(scope, node.name);
    if (v === undefined || typeof v !== 'object') throw new ExprError(`no such field: ${node.name}`);
    return v as Record<string, Scope>;
  }
  if (node.type === 'Member') {
    // Recurse: resolve the inner object, then index.
    const inner = resolveObject(node.object, scope);
    const id = (node.property as { type: string; value: number }).value;
    const key = _lookupStringPlaceholder(id);
    if (!(key in inner)) throw new ExprError(`no such field: ${key}`);
    const v = inner[key];
    if (v === undefined || typeof v !== 'object') throw new ExprError(`not a map: ${key}`);
    return v as Record<string, Scope>;
  }
  // Evaluate fully then expect a map.
  throw new ExprError('expected a map on the left of member access');
}
function toNum(v: Scope): number {
  if (typeof v === 'number') return v;
  throw new ExprError('expected a number');
}

// ─── Public convenience ─────────────────────────────────────────────────────
/** Parse + evaluate. Errors propagate as ExprError; callers surface them.
 *  Accepts a loose scope (`Record<string, unknown>`) so callers don't fight a
 *  recursive type; the walkers narrow internally. */
export function evalExpr(expr: string, scope: Record<string, unknown> | Scope): number {
  return evaluate(parse(expr), scope as Scope);
}

/** Try-style: returns null on error (for live lint in the editor). */
export function tryEvalExpr(expr: string, scope: Record<string, unknown> | Scope): { value: number | null; error: string | null } {
  try {
    return { value: evalExpr(expr, scope), error: null };
  } catch (e) {
    return { value: null, error: e instanceof Error ? e.message : String(e) };
  }
}
